import { ComponentProps, forwardRef, useRef } from 'react';
import { withStyles } from '../../../utils';
import styles from './MainButton.module.scss';
import MaterialSymbol from '../../DataDisplay/MaterialSymbol/MaterialSymbol';
import { useDatadogLog } from '../logging';
import useTextColor from './useTextColor';

const cn = withStyles(styles);

export type ColorScheme = 'primary' | 'secondary' | 'feature' | 'destructive';
type Size = 'large' | 'standard' | 'small' | 'smallest';
type IconProps = ComponentProps<typeof MaterialSymbol>;

type MainButtonProps = React.ComponentProps<'button'> & {
  colorScheme?: ColorScheme;
  size?: Size;
  iconLeft?: IconProps['icon'];
  iconLeftProps?: IconProps;
  iconRight?: IconProps['icon'];
  iconRightProps?: IconProps;
};

const MainButton = forwardRef<HTMLButtonElement, MainButtonProps>(
  (
    {
      children,
      colorScheme = 'secondary',
      size = 'standard',
      className,
      style,
      iconLeft,
      iconLeftProps,
      iconRight,
      iconRightProps,
      ...props
    },
    forwardedRef,
  ) => {
    const ref = useRef<HTMLButtonElement | null>(null);
    const textColor = useTextColor({
      ref,
      colorScheme,
    });
    const log = useDatadogLog();

    const combinedRef = (node: HTMLButtonElement | null) => {
      ref.current = node;

      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else if (forwardedRef && 'current' in forwardedRef) {
        forwardedRef.current = node;
      }
    };

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      const label =
        typeof children === 'string' ? children : (props['aria-label'] ?? e.currentTarget.textContent ?? 'Button');

      log({
        message: `${label} clicked`,
        payload: { name: label },
        actionType: 'Button',
      });

      if (props.onClick) {
        props.onClick(e);
      }
    };

    return (
      <button
        ref={combinedRef}
        type="button"
        {...props}
        className={cn(
          'button',
          `color-${colorScheme}`,
          `size-${size}`,
          (iconLeftProps || iconLeft) && 'iconLeft',
          (iconRightProps || iconRight) && 'iconRight',
          className,
        )}
        style={{
          color: textColor,
          borderRadius: `var(--buttonRadius)`,
          ...style,
        }}
        onClick={handleClick}
        data-custom="true"
      >
        {iconLeftProps && <MaterialSymbol {...iconLeftProps} className={cn('icon', iconLeftProps.className)} />}
        {iconLeft && <MaterialSymbol className={cn('icon')} icon={iconLeft} />}
        {children}
        {iconRight && <MaterialSymbol className={cn('icon')} icon={iconRight} />}
        {iconRightProps && <MaterialSymbol {...iconRightProps} className={cn('icon', iconRightProps.className)} />}
      </button>
    );
  },
);

MainButton.displayName = 'MainButton';

export default MainButton;
